import { Box, Chip, Paper, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { Check as AcceptIcon, Close as DeclineIcon } from "@mui/icons-material";
import type { Finding, FindingDecision } from "../../services/lensService";
import { formatWhen, isNotAssessed, severityColour } from "./lensUtils";

const useStyles = makeStyles()((theme) => ({
    card: { padding: theme.spacing(3), marginBottom: theme.spacing(2), borderLeftWidth: 4, borderLeftStyle: "solid" },
    compact: { padding: theme.spacing(2), marginBottom: 0 },
    critical: { borderLeftColor: theme.palette.error.main },
    material: { borderLeftColor: theme.palette.warning.main },
    preference: { borderLeftColor: theme.palette.info.main },
    notAssessedCard: { borderLeftStyle: "dashed", borderLeftColor: "#999" },
    declined: { opacity: 0.72 },
    head: { display: "flex", alignItems: "center", gap: theme.spacing(1), flexWrap: "wrap", marginBottom: theme.spacing(1) },
    spacer: { flexGrow: 1 },
    notAssessedChip: { color: "#777", borderColor: "#999", borderStyle: "dashed", fontWeight: 700, textTransform: "uppercase" },
    citation: { color: "#777", fontStyle: "italic", marginTop: theme.spacing(0.5) },
    box: { backgroundColor: "#f6f3ff", borderRadius: 8, padding: theme.spacing(2), marginTop: theme.spacing(2) },
    line: { marginBottom: theme.spacing(1), "&:last-child": { marginBottom: 0 } },
    label: {
        display: "block",
        fontSize: 10.5,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "#777",
        fontWeight: 700,
        marginBottom: 2,
    },
    del: { textDecoration: "line-through", textDecorationColor: theme.palette.error.main, color: "#555" },
    ins: { color: theme.palette.success.dark, fontWeight: 500 },
    missing: { color: "#999", fontStyle: "italic" },
    evidence: { fontStyle: "italic", color: "#555" },
    faint: { marginTop: theme.spacing(1), color: "#777" },
    declinedNote: { marginTop: theme.spacing(1), color: theme.palette.error.main, fontWeight: 500 },
    clientNote: { marginTop: theme.spacing(2), paddingTop: theme.spacing(2), borderTop: "1px solid #eee" },
}));

const DECIDED_LABEL: Record<FindingDecision, string> = { undecided: "", accepted: "Accepted", declined: "Declined" };

interface FindingCardProps {
    finding: Finding;
    isDeciding?: boolean;
    // Tighter, and without the client comment, for the side-by-side panel.
    compact?: boolean;
    // Absent: the card is read-only.
    onDecide?: (decision: FindingDecision) => void;
}

const FindingCard = ({ finding, isDeciding = false, compact = false, onDecide }: FindingCardProps) => {
    const { classes, cx } = useStyles();
    const fix = finding.fix;
    const notAssessed = isNotAssessed(finding);
    const decision = finding.decision;
    const isDeclined = decision === "declined" && !notAssessed;
    const decidedTitle = finding.decidedOn
        ? `${DECIDED_LABEL[decision]} ${formatWhen(finding.decidedOn)}${finding.decidedByName ? ` by ${finding.decidedByName}` : ""}`
        : DECIDED_LABEL[decision];

    return (
        <Paper
            variant="outlined"
            className={cx(classes.card, compact && classes.compact, notAssessed ? classes.notAssessedCard : classes[finding.severity], isDeclined && classes.declined)}
        >
            <Box className={classes.head}>
                {!notAssessed && <Chip size="small" color={severityColour[finding.severity]} label={finding.severity} />}
                {finding.type === "omission" && <Chip size="small" variant="outlined" label="omission" />}
                {notAssessed && (
                    <Tooltip title="This section matched no provision in the playbook, so Lens did not assess it. A human needs to review it.">
                        <Chip size="small" variant="outlined" label="not assessed" className={classes.notAssessedChip} />
                    </Tooltip>
                )}
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    {finding.provisionName}
                </Typography>
                {!notAssessed && decision !== "undecided" && (
                    <Tooltip title={decidedTitle}>
                        <Chip
                            size="small"
                            variant="outlined"
                            color={decision === "accepted" ? "success" : "error"}
                            label={DECIDED_LABEL[decision]}
                        />
                    </Tooltip>
                )}
                {onDecide && !notAssessed && (
                    <>
                        <span className={classes.spacer} />
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={decision === "undecided" ? null : decision}
                            // Clicking the selected button hands back null, which takes the verdict back off.
                            onChange={(_e, value: FindingDecision | null) => onDecide(value ?? "undecided")}
                            disabled={isDeciding}
                            aria-label={`Decision for ${finding.provisionName}`}
                        >
                            <ToggleButton value="accepted" color="success" aria-label="Accept">
                                <AcceptIcon fontSize="small" />
                                &nbsp;Accept
                            </ToggleButton>
                            <ToggleButton value="declined" color="error" aria-label="Decline">
                                <DeclineIcon fontSize="small" />
                                &nbsp;Decline
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </>
                )}
            </Box>

            {finding.message && <Typography variant="body2">{finding.message}</Typography>}
            {finding.citation && (
                <Typography variant="caption" component="div" className={classes.citation}>
                    {finding.citation}
                </Typography>
            )}

            {finding.evidence && (
                <Box className={classes.box}>
                    <span className={classes.label}>{notAssessed ? "Section text" : "Evidence"}</span>
                    <Typography variant="body2" className={classes.evidence}>
                        {finding.evidence}
                    </Typography>
                </Box>
            )}

            {notAssessed && (
                <>
                    {finding.paragraphIndex != null && (
                        <Typography variant="caption" component="div" className={classes.faint}>
                            Paragraph {finding.paragraphIndex} of the reviewed document
                            {finding.heading ? ` · ${finding.heading}` : ""}
                        </Typography>
                    )}
                    <Typography variant="caption" component="div" className={classes.faint} style={{ fontStyle: "italic" }}>
                        Nothing to accept or decline — no amendment has been proposed. The marked-up copy carries this as a
                        comment so the point is on the record.
                    </Typography>
                </>
            )}

            {fix && (
                <Box className={classes.box}>
                    {fix.mode === "insertclause" ? (
                        <>
                            <div className={classes.line}>
                                <span className={classes.label}>Current</span>
                                <span className={classes.missing}>Not in the document — clause is absent entirely</span>
                            </div>
                            <div className={classes.line}>
                                <span className={classes.label}>Suggested new clause</span>
                                <span className={classes.ins}>{fix.text}</span>
                            </div>
                        </>
                    ) : fix.mode === "appendsentence" ? (
                        <div className={classes.line}>
                            <span className={classes.label}>Suggested addition</span>
                            <span className={classes.ins}>{(fix.text ?? "").trim()}</span>
                        </div>
                    ) : (
                        <>
                            {fix.find && (
                                <div className={classes.line}>
                                    <span className={classes.label}>Current wording (to be removed)</span>
                                    <span className={classes.del}>{fix.find}</span>
                                </div>
                            )}
                            <div className={classes.line}>
                                <span className={classes.label}>Suggested replacement</span>
                                <span className={classes.ins}>{fix.text}</span>
                            </div>
                        </>
                    )}
                </Box>
            )}

            {isDeclined && (
                <Typography variant="caption" component="div" className={classes.declinedNote}>
                    {fix
                        ? "Declined — this amendment will not be written into the marked-up copy. The comment below still will."
                        : "Declined — the point stays on the record as a comment in the marked-up copy. Nothing is written into the document."}
                </Typography>
            )}

            {finding.clientNote && !compact && (
                <Box className={classes.clientNote}>
                    <span className={classes.label}>Client comment</span>
                    <Typography variant="body2">
                        {notAssessed ? "NOT ASSESSED" : finding.severity.toUpperCase()} — {finding.clientNote}
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default FindingCard;
